import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { ComputerDesktopIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline'
import { authAPI } from '../../services/api'
import { describeUserAgent } from '../../utils/describeUserAgent'
import { SESSIONS_QUERY_KEY } from './sessionsQueries'
import { wasCancelled, withConfirmation } from '../security/confirmCode'

const MOBILE = /iPhone|iPad|Android|Mobile/i

// The server sends naive UTC (no zone); read it as UTC.
const asUtc = (value) => new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)

/**
 * VH-284 — the user's open sessions on the profile page: device and browser,
 * when each started and was last active, "This device" for the current one,
 * "End" for each other one and "End all others".
 *
 * Ending one that had ended already counts as done (the server says so); the
 * list is read again after every end. VH-285: ending another session asks for
 * a current code when the last one is more than five minutes old.
 */
export function SessionList() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()

  const sessions = useQuery({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: () => authAPI.getSessions().then((response) => response.data.sessions),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY })

  const endOne = useMutation({
    mutationFn: (id) => withConfirmation(() => authAPI.endSession(id)),
    onSuccess: () => {
      toast.success(t('openSessions.ended'))
      refresh()
    },
    onError: (error) => !wasCancelled(error) && toast.error(t('openSessions.endError')),
  })

  const endOthers = useMutation({
    mutationFn: () => withConfirmation(() => authAPI.logoutOthers()),
    onSuccess: (response) => {
      toast.success(t('openSessions.othersEnded', { count: response.data.ended }))
      refresh()
    },
    onError: (error) => !wasCancelled(error) && toast.error(t('openSessions.endError')),
  })

  const when = (value) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(asUtc(value))

  const rows = sessions.data || []
  const others = rows.filter((row) => !row.current)

  return (
    <section aria-labelledby="open-sessions-title" className="space-y-3">
      <div>
        <h4 id="open-sessions-title" className="font-medium text-gray-900">
          {t('openSessions.title')}
        </h4>
        <p className="text-sm text-gray-500">{t('openSessions.description')}</p>
      </div>

      {sessions.isLoading && <div className="h-16 animate-pulse rounded-lg bg-gray-100" />}

      {sessions.isError && (
        <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <span>{t('openSessions.loadError')}</span>
          <button type="button" onClick={() => sessions.refetch()} className="font-medium underline">
            {t('openSessions.retry')}
          </button>
        </div>
      )}

      {sessions.isSuccess && (
        <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
          {rows.map((row) => {
            const Icon = MOBILE.test(row.user_agent || '') ? DevicePhoneMobileIcon : ComputerDesktopIcon
            return (
              <li key={row.id} className="flex items-center justify-between gap-3 p-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Icon className="h-6 w-6 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {describeUserAgent(row.user_agent) || t('openSessions.unknownDevice')}
                      {row.current && (
                        <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          {t('openSessions.thisDevice')}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('openSessions.started', { when: when(row.started_at) })}
                      {' · '}
                      {t('openSessions.lastActive', { when: when(row.last_active_at) })}
                      {row.address ? ` · ${row.address}` : ''}
                    </p>
                  </div>
                </div>
                {!row.current && (
                  <button
                    type="button"
                    onClick={() => endOne.mutate(row.id)}
                    disabled={endOne.isPending}
                    className="btn-secondary flex-shrink-0 text-sm"
                  >
                    {t('openSessions.end')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {others.length > 0 && (
        <button
          type="button"
          onClick={() => endOthers.mutate()}
          disabled={endOthers.isPending}
          className="btn-secondary text-sm"
        >
          {t('openSessions.endAllOthers')}
        </button>
      )}
    </section>
  )
}
